import { Controller } from '@hotwired/stimulus';
import { postGeneratorOpenAI, getPaywallController } from '../lib/generator_api';
import {
  applyDisplayFormula,
  bindFormulaDelimiterListener,
  delimiterPromptInstruction,
  processGeneratedFormula,
  unbindFormulaDelimiterListener,
} from '../lib/generator_formula_delimiter';

export default class extends Controller {
  static targets = [
    'usecase',
    'texttomatch',
    'cell',
    'submit',
    'submitLabel',
    'placeholder',
    'loading',
    'output',
    'formulaText',
    'error',
    'successNote',
    'copyButton'
  ];

  static values = {
    tool: { type: String, default: 'regexmatcher' }
  };

  connect() {
    bindFormulaDelimiterListener(this);
    this.formulaValue = '';
  }

  disconnect() {
    unbindFormulaDelimiterListener(this);
  }

  paywall() {
    return getPaywallController(this.element);
  }

  parseExample(event) {
    const raw = event.currentTarget.dataset.example;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      console.error('Invalid example payload');
      return null;
    }
  }

  loadExample(event) {
    const data = this.parseExample(event);
    if (!data) return;

    this.usecaseTarget.value = data.usecase;
    this.texttomatchTarget.value = data.sample;
    this.cellTarget.value = 'A1';
    this.clearError();
    this.hideResult();
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
    this.usecaseTarget.focus();
  }

  async applyFormula(event) {
    const data = this.parseExample(event);
    if (!data?.formula) return;

    const { canonical, display } = processGeneratedFormula(data.formula);
    this.canonicalFormula = canonical;
    this.formulaValue = display;
    await this.copyToClipboard(display);
    this.showResult(display);
    this.highlightExampleCard(event.currentTarget);
    this.scrollToGenerator();
  }

  highlightExampleCard(button) {
    this.element.querySelectorAll('[data-example-card]').forEach((card) => {
      card.classList.remove('ring-2', 'ring-violet-500/60', 'ring-offset-2');
    });
    button.closest('[data-example-card]')?.classList.add('ring-2', 'ring-violet-500/60', 'ring-offset-2');
  }

  scrollToGenerator() {
    document.getElementById('regexmatch-generator')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const usecase = this.usecaseTarget.value.trim();
    const texttomatch = this.texttomatchTarget.value.trim();

    if (!usecase || !texttomatch) {
      this.showError('Describe your use case and add an example string.');
      return;
    }

    const cellRaw = this.cellTarget.value;
    const cellValue = !cellRaw || cellRaw.trim() === '' ? 'A1' : cellRaw.trim();
    const userRole =
      'You are helping to figure out the regular expression needed to match a string of text. Only include the regular expression, not the words response in your response.';
    const prompt =
      String.raw`Here are two examples: Use Case: Check if a cell contains a valid email address. Text to Match: john.doe@example.com Response: [\w\-]+(\.[\w\-]+)*@[\w\-]+(\.[\w\-]+)*(\.\w{2,3})+ Prompt: Verify if a cell contains a valid phone number. Text to Match: 987-123-4567 Response: \d{3}-\d{3}-\d{4} This is the use case: ` +
      usecase +
      ' Text to match: ' +
      texttomatch +
      delimiterPromptInstruction();

    this.setLoading(true);
    this.hideResult();

    try {
      const result = await postGeneratorOpenAI({
        userRole,
        prompt,
        generator: this.toolValue
      });

      if (result.limited) {
        this.paywall()?.handleLimitReached();
        return;
      }

      if (result.error) {
        console.error('Regex matcher proxy error', result.response?.status, result.detail);
        this.showError('Could not generate a pattern. Try again in a moment.');
        return;
      }

      const data = result.data;
      const regexpression = data.choices?.[0]?.message?.content?.trim();

      if (!regexpression) {
        console.error('OpenAI proxy returned no regex', data);
        this.showError('No pattern came back. Add more detail and try again.');
        return;
      }

      const output = '=REGEXMATCH(' + cellValue + ',"' + regexpression + '")';
      const { canonical, display } = processGeneratedFormula(output);
      this.canonicalFormula = canonical;
      this.formulaValue = display;
      await this.copyToClipboard(display);
      this.paywall()?.applyUsageFromResponse(data.generator_usage);
      this.showResult(display);

      if (data.generator_usage?.remaining === 1) {
        this.showSuccessNudge();
      }
      if (data.generator_usage?.remaining === 0) {
        this.paywall()?.handleLimitReached();
      }
    } catch (err) {
      console.error('Regex matcher request failed', err);
      this.showError('Connection failed. Check your network and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async copy(event) {
    event?.preventDefault();
    if (!this.formulaValue) return;

    await this.copyToClipboard(this.formulaValue);
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied again.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard may be blocked; formula remains visible in the output panel.
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      const paywall = this.paywall();
      const atLimit = paywall && !paywall.paidMemberValue && paywall.remainingValue === 0;
      if (atLimit) {
        paywall.syncUi(0);
      } else {
        this.submitTarget.disabled = isLoading;
      }
    }
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isLoading ? 'Generating…' : 'Generate';
    }
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle('hidden', !isLoading);
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.toggle('hidden', isLoading);
    if (isLoading && this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (isLoading && this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (isLoading && this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  hideResult() {
    if (this.hasOutputTarget) this.outputTarget.classList.add('hidden');
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.add('hidden');
    if (this.hasSuccessNoteTarget) this.successNoteTarget.classList.add('hidden');
  }

  showResult(formula) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add('hidden');
    applyDisplayFormula(this, formula);
    if (this.hasOutputTarget) this.outputTarget.classList.remove('hidden');
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.remove('hidden');
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Copied to clipboard. Paste into your sheet.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showSuccessNudge() {
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = 'Nice. 1 free generation left on this tool.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove('hidden');
    }
    this.hideResult();
  }
}
